/*
 * lw-reviews.js — live Judge.me review data for custom HTML pages.
 *
 * WHY THIS EXISTS
 * Custom landing pages used to hard-code review counts. They drifted: the About hero said
 * "3,400+", the About proof bar said "1,400+", the Reviews page said "2,400+". Hard-coded
 * social proof is wrong the day after you write it. This reads the real numbers at page load.
 *
 * SOURCE OF TRUTH
 * Judge.me writes the standard Shopify review metafields on each product:
 *   reviews.rating        (type `rating`, JSON: {"scale_min","scale_max","value"})
 *   reviews.rating_count  (number_integer)
 * Both are exposed to the Storefront API, so the browser can read them with the public
 * storefront token. These are the same metafields Google Merchant Center reads for Product
 * Ratings, so if they are wrong, the Google feed is wrong too.
 *
 * DO NOT use the `reviews.summary` metafield — it is a stale Okendo leftover and disagrees
 * with rating_count (e.g. healthy-mouth-healthy-you: rating_count 7, summary.reviewCount 6).
 *
 * USAGE — put a sensible fallback in the HTML; JS replaces it. Never ship an empty element.
 *   <span data-lw-review-count="remineralizing-tooth-powder">500+</span>
 *   <span data-lw-review-rating="remineralizing-tooth-powder">4.9</span>
 *   <span data-lw-review-stars="remineralizing-tooth-powder"></span>
 *   <span data-lw-reviews="remineralizing-tooth-powder"></span>   full: stars + rating + count
 *   <span data-lw-review-total>3,800+</span>                      sitewide count
 *   <span data-lw-review-avg>4.8</span>                           sitewide average
 *
 * SEVERAL PRODUCTS IN ONE NUMBER (added 2026-09-21) — give a comma-separated handle list:
 *   <span data-lw-review-count="remineralizing-tooth-powder,whitening-tooth-powder">1,600+</span>
 *       -> the SUM of the counts
 *   <span data-lw-review-rating="remineralizing-tooth-powder,whitening-tooth-powder">4.8</span>
 *       -> the COUNT-WEIGHTED average rating (also works for -stars and data-lw-reviews)
 * A list only paints when EVERY handle in it resolved to a real product; one typo or a failed
 * request keeps the HTML fallback rather than showing a partial sum. A single handle behaves
 * exactly as it always has.
 *
 * Every product handle on the page is collected and fetched in ONE batched query, so a page
 * with twelve products still makes a single request. Results cache in sessionStorage.
 *
 * Sitewide totals come from assets/lw-review-totals.json, regenerated by the scheduled task
 * `lw-review-totals-refresh`. Summing 250 products in the browser on every page load would be
 * absurd; this mirrors the assets/directory-data.json pattern already used by the directory.
 */
(function () {
  'use strict';

  var SF_TOKEN = 'c3fd9e33ab2c993e2b9b2bc0326e0ca5';
  var SF_URL = '/api/2025-07/graphql.json';
  var CACHE_KEY = 'lw_reviews_v1';

  /* Sitewide totals are BAKED IN at build time by build_review_totals.py, not fetched.
     A custom page's body is never evaluated as Liquid, so `asset_url` is unavailable to it,
     and a hardcoded /cdn/shop/t/<id>/ path would silently break on the next theme publish
     (which is exactly how the RE-MINTS promo was lost on 2026-07-31). Baking them in removes
     the request and the failure mode. Per-product numbers below are still fetched live. */
  var TOTALS = {"generated_at": "2026-11-02T14:21:51Z", "products_with_reviews": 84, "total_reviews": 4162, "average_rating": 4.76};

  /* Double-load guard (added 2026-09-21). page.custom / page.fullnav load this file through a
     versioned asset_url tag, and ~35 page bodies ALSO carry an old hard-coded <script> for it.
     Both are `defer`, so they run in document order. The newest build always wins: a copy only
     runs if no copy with the same or a newer bake date has already run on this page. */
  var BUILD = (TOTALS && TOTALS.generated_at) || '';
  if (window.__lwReviewsBuild && window.__lwReviewsBuild >= BUILD) return;
  window.__lwReviewsBuild = BUILD || '0';

  function esc(h) { return String(h).replace(/[^a-z0-9-]/gi, ''); }

  /* An attribute value is one handle or a comma-separated list of handles. */
  function handlesOf(v) {
    return String(v || '').split(',').map(esc).filter(function (h) { return h; });
  }

  /* One handle: the product's own numbers (null when it has no reviews, so the fallback stays).
     Several: summed count + count-weighted average, only if every handle resolved. */
  function resolve(data, v) {
    var hs = handlesOf(v);
    if (!hs.length) return null;
    if (hs.length === 1) {
      var one = data[hs[0]];
      return one && !one.empty ? one : null;
    }
    var count = 0, weighted = 0, ratedCount = 0;
    for (var i = 0; i < hs.length; i++) {
      var d = data[hs[i]];
      if (!d) return null;
      var c = d.count || 0;
      count += c;
      if (d.rating != null && c) { weighted += d.rating * c; ratedCount += c; }
    }
    if (!count) return null;
    return { count: count, rating: ratedCount ? weighted / ratedCount : null };
  }
  function num(n) { return Number(n).toLocaleString('en-US'); }

  /* Stars are drawn, not fonted, so they render identically everywhere and need no icon set.
     Gold #D6A35D is the brand accent; stars are the one decorative place it is allowed. */
  function stars(rating) {
    var pct = Math.max(0, Math.min(100, (Number(rating) / 5) * 100));
    return (
      '<span class="lwrv-stars" role="img" aria-label="' +
      Number(rating).toFixed(1) + ' out of 5 stars">' +
      '<span class="lwrv-stars-bg">★★★★★</span>' +
      '<span class="lwrv-stars-fg" style="width:' + pct + '%">★★★★★</span>' +
      '</span>'
    );
  }

  function collectHandles() {
    var attrs = ['data-lw-review-count', 'data-lw-review-rating', 'data-lw-review-stars', 'data-lw-reviews'];
    var set = {};
    attrs.forEach(function (a) {
      document.querySelectorAll('[' + a + ']').forEach(function (el) {
        handlesOf(el.getAttribute(a)).forEach(function (h) { set[h] = true; });
      });
    });
    return Object.keys(set);
  }

  function readCache() {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY)) || {}; } catch (e) { return {}; }
  }
  function writeCache(d) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(d)); } catch (e) { /* private mode */ }
  }

  function fetchProducts(handles) {
    var cache = readCache();
    var missing = handles.filter(function (h) { return !cache[h]; });
    if (!missing.length) return Promise.resolve(cache);

    var q = missing.map(function (h, i) {
      return 'p' + i + ': productByHandle(handle: "' + h + '") {' +
        ' rc: metafield(namespace: "reviews", key: "rating_count") { value }' +
        ' rr: metafield(namespace: "reviews", key: "rating") { value } }';
    }).join('\n');

    return fetch(SF_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Shopify-Storefront-Access-Token': SF_TOKEN },
      body: JSON.stringify({ query: '{\n' + q + '\n}' })
    })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var d = (j && j.data) || {};
        missing.forEach(function (h, i) {
          var p = d['p' + i];
          if (!p) return;
          var rating = null;
          if (p.rr && p.rr.value) {
            try { rating = parseFloat(JSON.parse(p.rr.value).value); } catch (e) { rating = null; }
          }
          var count = p.rc && p.rc.value ? parseInt(p.rc.value, 10) : null;
          if (count || rating) cache[h] = { count: count, rating: rating };
          else cache[h] = { count: 0, rating: null, empty: true }; /* real product, no reviews yet */
        });
        writeCache(cache);
        return cache;
      })
      .catch(function () { return cache; }); /* network failure keeps the HTML fallback */
  }

  function paintProducts(data) {
    document.querySelectorAll('[data-lw-review-count]').forEach(function (el) {
      var d = resolve(data, el.getAttribute('data-lw-review-count'));
      if (d && d.count != null) el.textContent = num(d.count);
    });
    document.querySelectorAll('[data-lw-review-rating]').forEach(function (el) {
      var d = resolve(data, el.getAttribute('data-lw-review-rating'));
      if (d && d.rating != null) el.textContent = d.rating.toFixed(1);
    });
    document.querySelectorAll('[data-lw-review-stars]').forEach(function (el) {
      var d = resolve(data, el.getAttribute('data-lw-review-stars'));
      if (d && d.rating != null) el.innerHTML = stars(d.rating);
    });
    document.querySelectorAll('[data-lw-reviews]').forEach(function (el) {
      var d = resolve(data, el.getAttribute('data-lw-reviews'));
      if (!d || d.rating == null) return;
      el.innerHTML =
        stars(d.rating) +
        '<span class="lwrv-score">' + d.rating.toFixed(1) + '</span>' +
        (d.count ? '<span class="lwrv-count">(' + num(d.count) + ' reviews)</span>' : '');
    });
  }

  function paintTotals() {
    if (!TOTALS || !TOTALS.total_reviews) return;
    document.querySelectorAll('[data-lw-review-total]').forEach(function (el) {
      el.textContent = num(TOTALS.total_reviews);
    });
    document.querySelectorAll('[data-lw-review-avg]').forEach(function (el) {
      el.textContent = Number(TOTALS.average_rating).toFixed(1);
    });
  }

  /* Styles ship inside this file so a page author includes ONE asset, and so the rules are
     scoped to .lwrv-* and cannot leak onto the theme header, footer or cart drawer. */
  function injectStyles() {
    if (document.getElementById('lwrv-styles')) return;
    var s = document.createElement('style');
    s.id = 'lwrv-styles';
    s.textContent =
      '.lwrv-stars{position:relative;display:inline-block;white-space:nowrap;' +
      'font-size:1em;line-height:1;letter-spacing:2px;vertical-align:middle}' +
      '.lwrv-stars-bg{color:rgba(28,28,28,0.20)}' +
      '.lwrv-stars-fg{position:absolute;top:0;left:0;overflow:hidden;color:#D6A35D}' +
      '.lwrv-score{font-family:Inter,sans-serif;font-weight:500;margin-left:8px;' +
      'color:rgba(28,28,28,0.85)}' +
      '.lwrv-count{font-family:Inter,sans-serif;margin-left:8px;color:rgba(28,28,28,0.85)}';
    document.head.appendChild(s);
  }

  function init() {
    injectStyles();
    var handles = collectHandles();
    if (handles.length) fetchProducts(handles).then(paintProducts);
    paintTotals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
